(ns storrito.instagram-connect.disconnect-reason
  "Maps PHP exception classes to user-friendly disconnect reasons and
   actionable recommendations.")

(def reason-mappings
  "Maps PHP exception class substrings to labels and recommendations.
   Order matters: first match wins."
  [{:pattern "AccountRecoveryRequiredException"
    :label "Instagram Security Check"
    :description "Instagram flagged this login as suspicious and asks you to secure the account."
    :recommendation :secure-account}
   ;; Before "Checkpoint": the risky contact point check sits in the
   ;; Checkpoint namespace but is no two-factor step (brain-131).
   {:pattern "UpdateRiskyContactpointChallengeException"
    :label "Instagram Contact Check"
    :description "Instagram asks you to confirm or update the phone number or email address of this account."
    :recommendation :confirm-contact-point}
   ;; No "Checkpoint" in this class name, so its position does not matter.
   {:pattern "UnknownTwoFactorScreenException"
    :label "Login Approval Needed"
    :description "Instagram wants this login approved in a way Storrito can't complete."
    :recommendation :approve-login}
   {:pattern "Checkpoint"
    :label "2FA Challenge"
    :description "Instagram requires a two-factor authentication challenge."
    :recommendation :add-totp}
   {:pattern "Missing2faSecretException"
    :label "Missing TOTP Secret"
    :description "Storrito can't solve the 2FA process for this account."}
   {:pattern "LoginRequiredException"
    :label "Session Expired"
    :description "The Instagram session has expired."
    :recommendation :reconnect}
   {:pattern "ForcedPasswordResetException"
    :label "Password Reset Required"
    :description "Instagram requires a password reset for this account."
    :recommendation :reset-password}
   {:pattern "IncorrectPasswordException"
    :label "Incorrect Password"
    :description "The stored password is no longer valid."
    :recommendation :reconnect-new-password}
   {:pattern "SentryBlockException"
    :label "Account Blocked"
    :description "Instagram has temporarily blocked this account."
    :recommendation :wait-and-reconnect}
   {:pattern "AccountDisabledException"
    :label "Account Disabled"
    :description "This Instagram account has been disabled."
    :recommendation :check-account}
   {:pattern "InvalidUserException"
    :label "Invalid User"
    :description "Instagram does not recognize this user."
    :recommendation :check-account}
   {:pattern "InvalidUsernameException"
    :label "Username Invalid"
    :description "The Instagram username is invalid."
    :recommendation :check-account}
   ;; Instagram's assistive login screen ("Is this your account?") at a
   ;; re-login during posting (brain-165).
   {:pattern "IdentificationErrorException"
    :label "Account Not Confirmed"
    :description "Instagram could not confirm an account for this username when Storrito logged in."
    :recommendation :confirm-username}
   {:pattern "AccountUploadRestrictedException"
    :label "Upload Restricted"
    :description "Instagram has restricted uploads for this account."
    :recommendation :wait-and-reconnect}
   {:pattern "AccountVerificationRequiredException"
    :label "Account Recovery Required"
    :description "Instagram is recovering this account and asks you to verify it in the Instagram app."
    :recommendation :check-account}
   {:pattern "UnexpectedLoginErrorException"
    :label "Login Error"
    :description "An unexpected error occurred during login."
    :recommendation :reconnect}
   {:pattern "NotResolvableException"
    :label "Unresolvable Error"
    :description "The error could not be resolved automatically."
    :recommendation :reconnect}])

(def recommendation-messages
  {:secure-account "Open the Instagram app, approve the login when asked (do not change the password), then reconnect."
   :confirm-contact-point "Open the Instagram app, confirm or update the phone number or email address Instagram shows, then reconnect."
   :approve-login "Open the Instagram app on a phone where this account is logged in, approve the login if asked, then reconnect."
   :reset-password "Reset your Instagram password, then reconnect."
   :check-account "Check your Instagram account status in the Instagram app."
   :confirm-username "Check that the username is still the account's current one. If it is, open the Instagram app, log in there and complete any security check, then reconnect."
   :wait-and-reconnect "Wait a few hours, then reconnect your account."})

(defn match-reason
  "Returns the first matching reason mapping for a PHP exception class string,
   or nil if no match."
  [disconnect-reason]
  (when (and (string? disconnect-reason)
             (seq disconnect-reason))
    (first (filter (fn [{:keys [pattern]}]
                     #?(:clj (.contains ^String disconnect-reason ^String pattern)
                        :cljs (.includes disconnect-reason pattern)))
                   reason-mappings))))

(defn get-label
  "Returns a user-friendly label for the given disconnect reason string,
   or \"Disconnected\" if unknown."
  [disconnect-reason]
  (or (:label (match-reason disconnect-reason))
      "Disconnected"))

(defn get-recommendation-text
  "Returns the recommendation text for the given disconnect reason string,
   or nil if unknown."
  [disconnect-reason]
  (when-let [matched (match-reason disconnect-reason)]
    (get recommendation-messages (:recommendation matched))))
